import React, { useState, useEffect } from "react";

import {
    Box,
    Paper,
    Divider,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Card,
    styled
} from '@mui/material';

import { useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from "react-router-dom"
import CommonBreadcumbs from '../common/CommonBreadcumbs';
import CommonSectionHeader from "../common/CommonSectionHeader";
import CommonRestricted from '../common/CommonRestricted';

import { useSystemRoutes, routeTypes } from '../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../hooks/usePermissions';

// import { useUsers, userTypes, targetTypes } from "../../hooks/api/useUsers";
import { useLogistics, transportTypes, logisticTypes, targetTypes as transportTargetTypes } from '../../hooks/api/useLogistics';
import { useSystemPrompt } from "../../hooks/useSystemPrompt";
import { useTranslations } from "../../hooks/useTranslations";

import { initialValues, validationSchema } from "../../schema/logistics/transporterSchema";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function NewTransporter({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "prompt", "@logistic"] })

    const { promptMessage } = useSystemPrompt();
    const { isLoading, create } = useLogistics({ logisticType: logisticTypes.transporter, targetType: transportTargetTypes.transporter });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.logistic.id, scopeTypes.logistic.transporter.id)

    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const onSubmit = async (currentValues) => {

        try {

            let data = {
                transporter: {
                    label: currentValues?.label,
                    type: currentValues?.type,
                    description: currentValues?.description,
                    remark: currentValues?.remark,
                    status: currentValues?.status ? 1 : 0,
                },
                props: {
                    vehicle_plate_no: currentValues?.vehiclePlateNo,
                    vehicle_model: currentValues?.vehicleModel,
                    vehicle_brand: currentValues?.vehicleBrand
                }
            }

            const response = await create({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("logistics/transporters"))

        } catch (error) {
            promptMessage(false, `Action Failed. (${error?.message})`)
        }

    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;

    const RenderForm = () => {

        return (

            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={0.5}>
                        <Grid item xs={12} sm={12} md={4} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t('logisticType')}
                            // subTitle={'Access authentication'}
                            />

                        </Grid>
                        <Grid item xs={12} sm={12} md={8}>

                            <Grid container spacing={0.5}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <FormControlLabel
                                        sx={{
                                            marginLeft: '1px'
                                        }}
                                        control={
                                            <Switch
                                                name="status"
                                                checked={Boolean(values.status)}
                                                onChange={(event, checked) => {
                                                    setFieldValue("status", checked);
                                                }}
                                            />
                                        }
                                        label={
                                            Boolean(values.status) ? t("enabled") : t("disabled")
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={12} md={6}>
                                    <FormControl
                                        margin="dense"
                                        fullWidth
                                        size="small"
                                        error={touched.type && Boolean(errors.type)}
                                    >
                                        <InputLabel id="type">{t('type')}</InputLabel>
                                        <Select
                                            labelId="type"
                                            label={t("type")}
                                            name={"type"}
                                            onChange={(event) => {
                                                setFieldValue("type", event.target.value)
                                            }
                                            }
                                            value={values.type}
                                            sx={{
                                                textAlign: 'left'
                                            }}
                                        >

                                            {
                                                transportTypes.map((i, index) => {
                                                    return (
                                                        <MenuItem key={index} value={i}>{i}</MenuItem>
                                                    )
                                                })
                                            }

                                        </Select>
                                        <FormHelperText>
                                            {touched.type && errors.type}
                                        </FormHelperText>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={6}>
                                    {/* <Item>xs=4</Item> */}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ mt: 1, mb: 1 }} />
                        </Grid>

                        <Grid item xs={12} sm={12} md={4} textAlign={"left"}>

                            <CommonSectionHeader
                                title={t('logisticInfo')}
                            // subTitle={'Full details of driver information'}
                            />

                        </Grid>
                        <Grid item xs={12} sm={12} md={8}>
                            <Grid container spacing={0.5}>
                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        autoFocus
                                        fullWidth
                                        name={"label"}
                                        label={t("label")}
                                        type="text"
                                        variant="outlined"
                                        value={values.label}
                                        onChange={handleChange}
                                        error={touched.label && Boolean(errors.label)}
                                        helperText={touched.label && errors.label}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"vehiclePlateNo"}
                                        label={t("vehiclePlateNo")}
                                        type="text"
                                        variant="outlined"
                                        value={values.vehiclePlateNo}
                                        onChange={handleChange}
                                        error={touched.vehiclePlateNo && Boolean(errors.vehiclePlateNo)}
                                        helperText={touched.vehiclePlateNo && errors.vehiclePlateNo}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"vehicleModel"}
                                        label={t("vehicleModel")}
                                        type="text"
                                        variant="outlined"
                                        value={values.vehicleModel}
                                        onChange={handleChange}
                                        error={touched.vehicleModel && Boolean(errors.vehicleModel)}
                                        helperText={touched.vehicleModel && errors.vehicleModel}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        name={"vehicleBrand"}
                                        label={t("vehicleBrand")}
                                        type="text"
                                        variant="outlined"
                                        value={values.vehicleBrand}
                                        onChange={handleChange}
                                        error={touched.vehicleBrand && Boolean(errors.vehicleBrand)}
                                        helperText={touched.vehicleBrand && errors.vehicleBrand}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        rows={3}
                                        multiline
                                        name={"description"}
                                        label={("description")}
                                        type="text"
                                        variant="outlined"
                                        value={values.description}
                                        onChange={handleChange}
                                        error={touched.description && Boolean(errors.description)}
                                        helperText={touched.description && errors.description}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        margin="dense"
                                        size="small"
                                        fullWidth
                                        rows={3}
                                        multiline
                                        name={"remark"}
                                        label={t("remark")}
                                        type="text"
                                        variant="outlined"
                                        value={values.remark}
                                        onChange={handleChange}
                                        error={touched.remark && Boolean(errors.remark)}
                                        helperText={touched.remark && errors.remark}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2 }}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>

        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.newTransporter })
            }

            <CommonBreadcumbs
                pageTitle={t("newTransporter")}
                breadcrumbs={[
                    {
                        displayText: t("transporters"),
                        props: {
                            // underline: "hover",
                            href: getRoute("logistics/transporters")
                        }
                    },
                    {
                        displayText: t("newTransporter"),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageNew.id }) ?
                    RenderForm()
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}